import { useLayoutEffect, useRef } from "react";
import { gsap } from "../../lib/motion";
import { PresentationVideo } from "./PresentationVideo";

const STEPS = [
  { k: "BUILD", t: "Construire", h: "Un site clair, rapide et crédible.",
    p: "Sites vitrines et boutiques en ligne pensés pour le mobile, avec les bases du référencement Google dès la conception." },
  { k: "INTEGRATE", t: "Connecter", h: "Vos outils qui se parlent.",
    p: "Catalogue, stocks, commandes, e-mails : les bonnes informations circulent entre vos logiciels, sans ressaisies inutiles." },
  { k: "AUTOMATE", t: "Automatiser", h: "Des assistants pour ce qui se répète.",
    p: "Assistants IA (ChatGPT, Claude, Gemini…) et automatisations encadrées, avec des validations humaines quand il le faut." },
];

const NODES = [
  { x: 260, y: 92, c: "m", l: "Boutique" }, { x: 120, y: 290, c: "c", l: "Stocks" }, { x: 400, y: 290, c: "a", l: "Commandes" },
  { x: 70, y: 120, c: "v", l: "E-mails" }, { x: 450, y: 120, c: "v", l: "CRM" },
];

const IntegrateSvg = () => (
  <svg viewBox="0 0 520 400" className="seq-svg" role="img" aria-label="Outils connectés autour d’un nœud central">
    {NODES.map((n) => <line key={n.l} className="link" x1="260" y1="210" x2={n.x} y2={n.y} />)}
    <line className="link alt" x1="70" y1="120" x2="260" y2="92" /><line className="link alt" x1="450" y1="120" x2="260" y2="92" />
    <line className="link alt" x1="120" y1="290" x2="400" y2="290" />
    <circle className="node hub" cx="260" cy="210" r="30" />
    <circle className="node hub-in" cx="260" cy="210" r="11" />
    {NODES.map((n) => (
      <g key={n.l} className={`node n-${n.c}`}>
        <circle cx={n.x} cy={n.y} r="22" />
        <text x={n.x} y={n.y + 44} textAnchor="middle">{n.l}</text>
      </g>
    ))}
  </svg>
);

const AutomateSvg = () => (
  <svg viewBox="0 0 520 400" className="seq-svg" role="img" aria-label="Flux automatisé avec assistants et validation">
    <path className="flow" d="M100 200 C 150 200, 150 90, 200 90" /><path className="flow" d="M100 200 C 150 200, 150 310, 200 310" />
    <path className="flow" d="M340 90 C 385 90, 385 200, 410 200" /><path className="flow" d="M340 310 C 385 310, 385 200, 410 200" />
    <g className="agent"><rect x="20" y="176" width="80" height="48" rx="12" /><text x="60" y="205" textAnchor="middle">Demande</text></g>
    <g className="agent a-v"><rect x="200" y="62" width="140" height="56" rx="14" /><text x="270" y="96" textAnchor="middle">Agent ventes</text></g>
    <g className="agent a-c"><rect x="200" y="282" width="140" height="56" rx="14" /><text x="270" y="316" textAnchor="middle">Agent support</text></g>
    <g className="agent a-a"><rect x="410" y="172" width="96" height="56" rx="14" /><text x="458" y="206" textAnchor="middle">Validation</text></g>
  </svg>
);

const VISUALS = [PresentationVideo, IntegrateSvg, AutomateSvg];

export const BuildSequence = () => {
  const root = useRef(null);
  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add("(min-width: 960px) and (prefers-reduced-motion: no-preference)", () => {
      const q = gsap.utils.selector(root);
      gsap.set(q(".seq-step"), { autoAlpha: 0 });
      gsap.set(q(".seq-step")[0], { autoAlpha: 1 });
      const tl = gsap.timeline({
        defaults: { ease: "power2.out" },
        scrollTrigger: { trigger: q(".seq-pin")[0], start: "top top", end: "+=2600", pin: true, scrub: 0.7, anticipatePin: 1 },
      });
      tl.to(q(".seq-bar i"), { scaleX: 0.333, duration: 0.6 }, 0)
        .to(q(".s-0"), { autoAlpha: 0, y: -30, duration: 0.35 }, "+=0.35")
        .fromTo(q(".s-1"), { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.35 })
        .from(q(".s-1 .link"), { strokeDashoffset: 260, stagger: 0.04, duration: 0.5 }, "<")
        .from(q(".s-1 .node"), { scale: 0, autoAlpha: 0, stagger: 0.05, duration: 0.4, transformOrigin: "center", transformBox: "fill-box" }, "<0.1")
        .to(q(".seq-bar i"), { scaleX: 0.666, duration: 0.6 }, "<")
        .to(q(".s-1"), { autoAlpha: 0, y: -30, duration: 0.35 }, "+=0.35")
        .fromTo(q(".s-2"), { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.35 })
        .from(q(".s-2 .flow"), { strokeDashoffset: 320, stagger: 0.06, duration: 0.55 }, "<")
        .from(q(".s-2 .agent"), { autoAlpha: 0, x: 18, stagger: 0.08, duration: 0.35 }, "<0.1")
        .to(q(".seq-bar i"), { scaleX: 1, duration: 0.6 }, "<")
        .to({}, { duration: 0.4 });
    });
    return () => mm.revert();
  }, []);

  return (
    <section className="seq" ref={root} aria-labelledby="seq-title" data-testid="build-sequence">
      <div className="seq-pin">
        <div className="seq-head">
          <span className="eyebrow">NOTRE MÉTHODE EN 3 MOUVEMENTS</span>
          <h2 id="seq-title" className="sr-only">Build, integrate, automate</h2>
          <div className="seq-bar" aria-hidden="true"><i /></div>
          <ol className="seq-labels" aria-hidden="true">{STEPS.map((s) => <li key={s.k}>{s.k}</li>)}</ol>
        </div>
        <div className="seq-stage">
          {STEPS.map((s, i) => {
            const V = VISUALS[i];
            return (
              <div className={`seq-step s-${i}`} key={s.k} data-testid={`seq-step-${s.k.toLowerCase()}`}>
                <div className="seq-text">
                  <span className="seq-k">{String(i + 1).padStart(2, "0")} · {s.k}</span>
                  <h3>{s.t}. <span className="grad-text">{s.h}</span></h3>
                  <p>{s.p}</p>
                </div>
                <div className="seq-visual"><V /></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
